import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import { DECLINE_REASON_MAX_LENGTH } from "src/shared/api-types/certificate/dto";

import Button from "src/components/Button";
import { TextareaField } from "src/components/FormRhf/Textarea";

/** The API's minimum after trimming (ResolvePendingCertificateBodySchema); api-types exports only the maximum. */
export const DECLINE_REASON_MIN_LENGTH = 3;

export { DECLINE_REASON_MAX_LENGTH };

interface DeclineForm {
  reason: string;
}

export interface DeclineCertificateDialogProps {
  open: boolean;
  /** "(courseId) course name" and the member's name, for the question. */
  certificate: string;
  user: string;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onDecline: (reason: string) => void;
}

/**
 * Declining a pending certificate needs a reason (CREW-577). It is stored with the decision and sent
 * to the person who uploaded the certificate.
 */
const DeclineCertificateDialog: FC<DeclineCertificateDialogProps> = ({
  open,
  certificate,
  user,
  saving,
  error,
  onCancel,
  onDecline,
}) => {
  const { t } = useTranslation();

  // The same rules as the API, checked while typing so a disabled Decline button always says why.
  const schema = useMemo(
    () =>
      z.object({
        reason: z
          .string()
          .trim()
          .min(DECLINE_REASON_MIN_LENGTH, {
            message: t("pending_certificates.decline_reason_required", { min: DECLINE_REASON_MIN_LENGTH }),
          })
          .max(DECLINE_REASON_MAX_LENGTH, {
            message: t("pending_certificates.decline_reason_too_long", { max: DECLINE_REASON_MAX_LENGTH }),
          }),
      }),
    [t],
  );

  const { control, handleSubmit, reset, watch, formState } = useForm<DeclineForm>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { reason: "" },
  });

  // Start empty every time, so a cancelled reason does not show up for the next certificate.
  useEffect(() => {
    if (open) reset({ reason: "" });
  }, [open, reset]);

  const length = watch("reason").trim().length;

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t("pending_certificates.decline_certificate")}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t("pending_certificates.confirm_decline", { certificate, user })}
        </DialogContentText>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextareaField<DeclineForm>
          name="reason"
          control={control}
          label={t("pending_certificates.decline_reason")}
          helperText={`${t("pending_certificates.decline_reason_hint")} ${length}/${DECLINE_REASON_MAX_LENGTH}`}
          disabled={saving}
          required
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          {t("cancel")}
        </Button>
        <Button
          color="error"
          disabled={saving || !formState.isValid}
          onClick={handleSubmit(({ reason }) => onDecline(reason))}
        >
          {t("pending_certificates.decline")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeclineCertificateDialog;
